/**
 * Catalogue des fournisseurs et obligés CEE référencés sur la plateforme.
 *
 * Rôles :
 *  - `oblige`       : obligé CEE (article L. 221-1 du Code de l'énergie)
 *  - `fournisseur`  : fournisseur d'énergie comparé dans nos outils
 *  - `mandataire`   : mandataire / délégataire CEE
 *
 * Les logos couleur vivent dans `/logos/{slug}.{ext}` et les variantes claires
 * pour fonds sombres dans `/logos/dark/{slug}.{ext}`.
 */

export type ProviderRole = 'oblige' | 'fournisseur' | 'mandataire';

export interface EnergyProvider {
  slug: string;
  name: string;
  logo: string;
  logoDark?: string;
  roles: readonly ProviderRole[];
  description?: string;
}

export const ENERGY_PROVIDERS: readonly EnergyProvider[] = [
  {
    slug: 'edf',
    name: 'EDF',
    logo: '/logos/edf.svg',
    logoDark: '/logos/dark/edf-scroll.png',
    roles: ['oblige', 'fournisseur'],
  },
  {
    slug: 'totalenergies',
    name: 'TotalEnergies',
    logo: '/logos/totalenergies.png',
    logoDark: '/logos/dark/totalenergies-mobile.png',
    roles: ['oblige', 'fournisseur'],
  },
  {
    slug: 'eni',
    name: 'Eni',
    logo: '/logos/eni.svg',
    roles: ['oblige', 'fournisseur'],
  },
  {
    slug: 'plenitude',
    name: 'Plenitude',
    logo: '/logos/plenitude.svg',
    roles: ['fournisseur'],
    description: 'Marque B2C d’Eni en France',
  },
  {
    slug: 'enilive',
    name: 'Enilive',
    logo: '/logos/enilive.svg',
    roles: ['oblige', 'fournisseur'],
    description: 'Mobilité & carburants — groupe Eni',
  },
  {
    slug: 'dyneff',
    name: 'Dynéff',
    logo: '/logos/dyneff.png',
    roles: ['oblige'],
    description: 'Distributeur carburants Q8 — obligé CEE carburants',
  },

  {
    slug: 'octopus',
    name: 'Octopus Energy',
    logo: '/logos/octopus.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'elmy',
    name: 'Elmy',
    logo: '/logos/elmy.svg',
    logoDark: '/logos/dark/elmy.svg',
    roles: ['fournisseur'],
  },
  {
    slug: 'mint-energie',
    name: 'Mint Énergie',
    logo: '/logos/mint-energie.svg',
    roles: ['fournisseur'],
  },
  {
    slug: 'ohm-energie',
    name: 'OHM Énergie',
    logo: '/logos/ohm-energie.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'alterna',
    name: 'Alterna',
    logo: '/logos/alterna.webp',
    logoDark: '/logos/dark/alterna.webp',
    roles: ['fournisseur'],
  },
  {
    slug: 'primeo',
    name: 'Primeo Énergie',
    logo: '/logos/primeo.png',
    logoDark: '/logos/dark/primeo.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'endesa',
    name: 'Endesa',
    logo: '/logos/endesa.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'enovos',
    name: 'Enovos',
    logo: '/logos/enovos.png',
    logoDark: '/logos/dark/enovos.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'akea-energies',
    name: 'Akéa Énergies',
    logo: '/logos/akea-energies.svg',
    roles: ['fournisseur'],
  },
  {
    slug: 'natgas',
    name: 'Natgas',
    logo: '/logos/natgas.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'met-france',
    name: 'MET France',
    logo: '/logos/met-france.webp',
    roles: ['fournisseur'],
  },
  {
    slug: 'selia',
    name: 'Selia',
    logo: '/logos/selia.png',
    roles: ['fournisseur'],
  },
  {
    slug: 'ilek',
    name: 'ilek',
    logo: '/logos/ilek.svg',
    logoDark: '/logos/variants/ilek-dark.svg',
    roles: ['fournisseur'],
    description: 'Électricité verte locale',
  },
  {
    slug: 'lucia-energie',
    name: 'Lucia Énergie',
    logo: '/logos/lucia-energie.svg',
    roles: ['fournisseur'],
  },
  {
    slug: 'save-energies',
    name: 'SAVE Energies',
    logo: '/logos/save-energies.png',
    roles: ['fournisseur'],
    description: 'Énergies renouvelables',
  },
  {
    slug: 'gaz-europeen',
    name: 'Gaz Européen',
    logo: '/logos/gaz-europeen.svg',
    roles: ['fournisseur'],
  },

  {
    slug: 'geg',
    name: 'GEG',
    logo: '/logos/geg.svg',
    roles: ['fournisseur'],
    description: 'Régie Grenoble',
  },
  {
    slug: 'gaz-de-bordeaux',
    name: 'Gaz de Bordeaux',
    logo: '/logos/gaz-de-bordeaux.svg',
    roles: ['fournisseur'],
  },
  {
    slug: 'gedia',
    name: 'GEDIA',
    logo: '/logos/gedia.svg',
    roles: ['fournisseur'],
    description: 'Régie Dreux',
  },
  {
    slug: 'energem',
    name: 'Énergem',
    logo: '/logos/energem.png',
    roles: ['fournisseur'],
    description: 'Groupe UEM Metz',
  },
  {
    slug: 'proxelia',
    name: 'Proxelia',
    logo: '/logos/proxelia.svg',
    logoDark: '/logos/dark/proxelia.svg',
    roles: ['fournisseur'],
    description: 'Régie Oise',
  },
  {
    slug: 'synelva',
    name: 'SYNELVA',
    logo: '/logos/synelva.png',
    roles: ['fournisseur'],
    description: 'ELD — Chartres',
  },

  {
    slug: 'hellio',
    name: 'Hellio',
    logo: '/logos/hellio.svg',
    roles: ['mandataire'],
  },
  {
    slug: 'idex',
    name: 'Idex',
    logo: '/logos/idex.svg',
    roles: ['mandataire'],
    description: 'Énergéticien & services énergie',
  },
];

export const PROVIDERS_BY_SLUG = Object.freeze(
  Object.fromEntries(ENERGY_PROVIDERS.map((p) => [p.slug, p])),
) as Readonly<Record<string, EnergyProvider>>;

export function getProvidersByRole(role: ProviderRole): readonly EnergyProvider[] {
  return ENERGY_PROVIDERS.filter((p) => p.roles.includes(role));
}
